/**
 * Insights. Read-only: nothing on this page can be edited.
 *
 * Four questions a teacher actually asks of a finished gradebook, in the order
 * they ask them: how are individual students moving, how did one assessment
 * land, where is the class sitting (and who is in trouble), and what does each
 * student's achievement-category profile look like.
 *
 * Deliberately NOT here: a completion check (the sheet's green already is one)
 * and a ranked student comparison (it turns an analysis screen into something
 * you would not project onto a wall).
 *
 * THE K/T/C/A BLOCK IS CONDITIONAL. Split assessments are opt-in per column, so
 * a teacher who never opted in has no category evidence at all - and for them
 * the achievement scatter would be four empty axes about work they never said
 * they were doing. It renders only when the range holds at least one split.
 *
 * PANEL STATE IS PANEL-LOCAL. Hovering a dot on the achievement scatter must
 * not light a line up in the trajectory chart - the two panels ask different
 * questions, and a shared hover briefly wired them together and read as a bug.
 *
 * COLOUR CARRIES NO IDENTITY ON THIS PAGE. Every student line is the same hue.
 * A categorical palette runs out at about eight, and this chart can hold a whole
 * class, so identity comes from the checkbox list and from hovering a line —
 * never from a colour a teacher would have to match against a legend. Drawn
 * differently: the class average (a neutral dashed REFERENCE rather than
 * another student), and a PINNED student on the scatter (amber - a deliberate
 * selection, not an identity assigned by the page).
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bar, BarChart, CartesianGrid, Line, LineChart, ReferenceLine,
  ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis,
} from "recharts";
import { ArrowLeft, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { PageContainer } from "@/layouts/PageContainer";
import { DateRangeFilter } from "@/gradebook/DateRangeFilter";
import {
  assessmentsInRange, defaultRange, markPercent, percentToLetter,
  SECTION_MAX_FIELD, SECTION_NAME, SECTION_SCORE_FIELD, SECTIONS, sectionsOf,
  truncateLabel, weightedAverage,
} from "@/gradebook/grading";
import { effectiveWeights, toWeightRows } from "@/gradebook/categories";
import { schoolYearRange } from "@/gradebook/schoolYear";
import { useGradebookData } from "@/gradebook/useGradebookData";

/* The app's chart conventions, matched to SchoolActivity.jsx. brand-600 is a
 * CSS var, so it re-steps itself in dark mode instead of being flipped. */
const SERIES = "hsl(var(--brand-600))";
const REFERENCE = "hsl(var(--muted-foreground))";
const GRID = "hsl(var(--border))";
/* 13px ticks and 13px bar labels: 11px was fine in a dashboard mock and
 * unreadable on a projector, which is where this page actually gets used. */
const TICK = { fontSize: 13, fill: "hsl(var(--muted-foreground))" };
const BAR_LABEL = { position: "top", fontSize: 13, fill: "hsl(var(--muted-foreground))" };

/** Growing Success letters at full grain - R through A+ - so the distribution
 *  shows the sub-bands a report card actually distinguishes, not five coarse
 *  families with the minuses and pluses averaged away. */
const LETTER_BINS = ["R", "D-", "D", "D+", "C-", "C", "C+", "B-", "B", "B+", "A-", "A", "A+"];

/** Ontario's pass mark, and the default the watch list opens on. */
const PASSING = 50;
/** How far above the boundary still counts as "close". */
const NEAR = 5;

/** A pinned student on the achievement scatter. Amber - the page's one
 *  DELIBERATE selection, not another data hue. */
const PINNED = "hsl(45 92% 47%)";

const mean = (xs) => xs.reduce((a, b) => a + b, 0) / xs.length;

/** A percent, or the same en dash the sheet puts in a section cell with nothing
 *  behind it (B4). Never a 0 - "no evidence" and "earned nothing" are different
 *  claims and a teacher acts on them differently. */
const pct = (p) => (p == null ? "–" : `${Math.round(p)}%`);

/** 0-9, 10-19 … 90-100. The last bin takes 100 so a perfect score has a home. */
const BINS = Array.from({ length: 10 }, (_, i) => ({
  label: i === 9 ? "90-100" : `${i * 10}-${i * 10 + 9}`,
  min: i * 10,
  max: i === 9 ? 100 : i * 10 + 9,
}));

/** One titled block. No explanatory copy - the title is the whole caption. */
function Panel({ title, note, children, className }) {
  return (
    <section className={cn("rounded-xl border border-border border-t-highlight bg-card p-5 shadow-card dark:bg-card-gradient", className)}>
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <h2 className="font-display text-lg leading-tight text-foreground">{title}</h2>
        {note && <span className="text-[13px] tabular-nums text-muted-foreground">{note}</span>}
      </div>
      {children}
    </section>
  );
}

/** A scrollable pick list beside a chart. Fixed height so the chart never moves
 *  as the roster grows, and the list scrolls rather than the page. */
function PickList({ children, actions }) {
  return (
    <div className="flex w-full shrink-0 flex-col sm:w-[190px]">
      {actions}
      <div className="max-h-[260px] min-h-[120px] overflow-y-auto rounded-md border border-border">
        {children}
      </div>
    </div>
  );
}

/** The achievement scatter's x labels, spelt out in full. "Knowledge and
 *  Understanding" is too long for one tick slot, so it wraps at its own
 *  "and" - the other three names stay single-line. */
function SectionTick({ x, y, payload }) {
  const name = SECTION_NAME[SECTIONS[payload.value]] ?? "";
  const parts = name.split(" and ");
  const lines = parts.length > 1 ? [`${parts[0]} and`, parts.slice(1).join(" and ")] : [name];
  return (
    <text x={x} y={y + 12} textAnchor="middle" fill="hsl(var(--muted-foreground))" fontSize={12.5}>
      {lines.map((line, i) => (
        <tspan key={i} x={x} dy={i === 0 ? 0 : 14}>{line}</tspan>
      ))}
    </text>
  );
}

/** Bar tooltip listing WHO is in the bin - the count already sits on the bar,
 *  so repeating it in the tooltip answered nothing. */
function NamesTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const names = payload[0].payload.names ?? [];
  return (
    <div className="max-w-[240px] rounded-lg border border-border bg-card px-3 py-2 shadow-card">
      {names.length === 0 ? (
        <p className="text-[12px] text-muted-foreground">Nobody in this band</p>
      ) : (
        names.map((n, i) => (
          <p key={i} className="text-[13px] leading-snug text-foreground">{n}</p>
        ))
      )}
    </div>
  );
}

/** One student's four numbers, on the card rail beside the achievement
 *  scatter - what a hover reads out to, and what a pin keeps there. */
function ProfileCard({ label, values, muted, pinned, onUnpin }) {
  return (
    <div
      className={cn(
        "shrink-0 rounded-md border px-3 py-2",
        pinned ? "border-[hsl(45_92%_47%)]" : "border-border",
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <p className={cn("truncate text-[13px] font-medium", muted ? "text-muted-foreground" : "text-foreground")}>
          {label}
        </p>
        {onUnpin && (
          <button
            type="button"
            onClick={onUnpin}
            title="Unpin"
            className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
          >
            <X size={13} />
          </button>
        )}
      </div>
      <div className="mt-1 grid grid-cols-4 gap-1">
        {SECTIONS.map((s) => (
          <div key={s} className="text-center" title={SECTION_NAME[s]}>
            <p className="text-[11px] font-semibold uppercase text-muted-foreground">{s}</p>
            <p className="text-[14px] font-semibold tabular-nums text-foreground">{pct(values[s])}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* The gradebook now arrives from `GradebookLayout`, which owns it for the whole
   `/gradebooks/:id` section. That is what stops a hop to Insights and back from
   re-downloading every mark in the book. The layout has already dealt with
   loading and failure, so `gradebook` here is never null and this component has
   no spinner or not-found branch of its own. */
export default function GradebookInsights() {
  const { gradebook } = useGradebookData();
  return <InsightsPanels key={gradebook.id} gradebook={gradebook} />;
}

function InsightsPanels({ gradebook }) {
  const assessments = gradebook.assessments;

  /* Nobody selected to start: a full class of overlapping lines answers no
   * question, so the chart opens with just the class average and drawing a
   * student's line is the deliberate act. */
  const [picked, setPicked] = useState(() => new Set());
  const [hovered, setHovered] = useState(null);
  const [focusAssessment, setFocusAssessment] = useState(() => assessments[0]?.id ?? null);
  const [boundary, setBoundary] = useState(PASSING);

  /* The achievement scatter's OWN hover and pins - deliberately not `hovered`:
   * pointing at a dot in one panel must not light anything up in another. */
  const [catHovered, setCatHovered] = useState(null);
  const [pinned, setPinned] = useState(() => new Set());
  const togglePin = (id) => setPinned((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  /* Opens on the span of the assessments that exist, never on the gradebook's
   * school-year label - `defaultRange`'s own header records what went wrong when
   * the label and the dates disagreed. */
  const fullRange = useMemo(
    () => defaultRange(assessments, gradebook.schoolYear, schoolYearRange),
    [assessments, gradebook.schoolYear],
  );
  const [range, setRange] = useState(() => fullRange);

  /* Weights come from ALL assessments, then the range filters the result. The
   * other order would silently re-weight the term every time a teacher moved a
   * date: a category holding four tests would jump from 25% to 100% of itself
   * because three of them fell outside the window. Same order as the sheet. */
  const weightOf = useMemo(
    () => effectiveWeights(toWeightRows(assessments), gradebook.categories),
    [assessments, gradebook.categories],
  );
  const columns = useMemo(
    () => assessmentsInRange(assessments, range.from, range.to)
      .map((a) => ({ ...a, weight: weightOf[a.id] ?? 0 })),
    [assessments, range, weightOf],
  );

  /** Every student's term percent, the number the class panels are built on. */
  const termOf = useMemo(() => {
    const out = {};
    for (const s of gradebook.students) {
      const marks = {};
      for (const a of columns) {
        const m = gradebook.marks[`${s.id}:${a.id}`];
        if (m) marks[a.id] = m;
      }
      out[s.id] = weightedAverage(columns, marks);
    }
    return out;
  }, [gradebook, columns]);

  const graded = gradebook.students.filter((s) => termOf[s.id] != null);

  /* ── Class distribution ──────────────────────────────────────────────── */
  /* Binned by the exact letter (D-, D, D+ …), through the same percentToLetter
   * the Grade column uses - so a bar here and a cell there can never disagree
   * about which side of a boundary a student sits on. */
  const distribution = useMemo(
    () => LETTER_BINS.map((letter) => {
      const members = graded.filter((s) => percentToLetter(termOf[s.id]) === letter);
      return { band: letter, students: members.length, names: members.map((s) => s.name) };
    }),
    [graded, termOf],
  );

  /* ── Close to and below the boundary ─────────────────────────────────── */
  const atRisk = useMemo(
    () => graded
      .filter((s) => termOf[s.id] < boundary + NEAR)
      .map((s) => ({ student: s, percent: termOf[s.id], below: termOf[s.id] < boundary }))
      .sort((a, b) => a.percent - b.percent),
    [graded, termOf, boundary],
  );

  /* ── Achievement categories (K/T/C/A) ────────────────────────────────── */
  /**
   * Every student's profile across the four achievement-chart categories, plus
   * the class shape and the raw counts the coverage banner is built from.
   *
   * WHY THIS IS THE POINT OF THE WHOLE MIGRATION: a term percent tells a teacher
   * a student is at 74 and nothing else. A profile tells them the 74 is strong
   * Knowledge carrying weak Thinking - which is the exact register an Ontario
   * report-card comment is written in ("demonstrates strong knowledge and
   * understanding; is developing thinking skills"). The sheet's footer already
   * gives one column's section averages; this gives the term's, per student.
   *
   * SOURCE: split assessments only, and only those in the current range. A
   * single-mark column carries no category label at all - inventing one for it,
   * or spreading its mark evenly over four categories, would fabricate the
   * evidence this page exists to audit.
   *
   * WEIGHTING - POOLED POINTS, i.e. weighted by each section's own `max`:
   * `sum(section scores) / sum(section maxes)`. A 20-mark Knowledge section is
   * twenty marks of evidence and a 5-mark one is five, so the first should move
   * the profile four times as far.
   *   - Rejected: the unweighted mean of the per-assessment section percents.
   *     That makes a 2-mark Communication box on a bellwork worth exactly as
   *     much as a 25-mark Communication task on a summative, and with short
   *     sections being both commoner and noisier (one mark is 50% on a 2-mark
   *     box) it hands the profile to whichever assessments matter least.
   *   - Rejected: reusing `weightedAverage`'s category-weight x share machinery.
   *     Plan A5 is explicit that K/T/C/A is DESCRIPTIVE - the sections roll up
   *     into the assessment total, and that total is what the weighting machine
   *     already consumes. Running the weights a second time at section level
   *     would emit four numbers that look like grades, invite being averaged
   *     back into one, and quietly become a second term mark disagreeing with
   *     the sheet's. Category weight is also per-ASSESSMENT, so it cannot
   *     discriminate between two sections of the same column anyway.
   *
   * STATUSES follow `markPercent` and the sheet's `sectionAverage` exactly, so
   * the same student cannot read differently on two pages. Excused drops out
   * entirely (no evidence). Missing is a zero on every section that column
   * assesses - the work was owed and not handed in, in all four categories at
   * once. An unmarked section on a `graded` cell is skipped rather than zeroed:
   * part-marking is the normal working state for as long as marking takes (B2),
   * and reading blanks as zeroes would report a Thinking crisis that is really
   * just a pile of unmarked paper.
   */
  const profile = useMemo(() => {
    const splits = columns.filter((a) => sectionsOf(a).length > 0);

    const rows = gradebook.students.map((student) => {
      const tally = Object.fromEntries(
        SECTIONS.map((s) => [s, { points: 0, max: 0, n: 0 }]),
      );
      for (const a of splits) {
        const m = gradebook.marks[`${student.id}:${a.id}`];
        if (!m || m.status === "excused") continue;
        for (const s of sectionsOf(a)) {
          const max = Number(a[SECTION_MAX_FIELD[s]]);
          if (!(max > 0)) continue;
          const t = tally[s];
          if (m.status === "missing") { t.max += max; t.n += 1; continue; }
          const score = m[SECTION_SCORE_FIELD[s]];
          if (score == null) continue;
          t.points += Number(score);
          t.max += max;
          t.n += 1;
        }
      }
      return {
        student,
        percent: Object.fromEntries(SECTIONS.map((s) => [
          s, tally[s].max > 0 ? (tally[s].points / tally[s].max) * 100 : null,
        ])),
        /* How many sections of evidence stand behind the percent, surfaced on
         * hover. A 68% built on one section is a guess; the same 68% built on
         * nine is a finding. A `missing` counts here - a zero the student owes
         * is evidence about the student, unlike a section nobody has marked. */
        count: Object.fromEntries(SECTIONS.map((s) => [s, tally[s].n])),
      };
    });

    /* The class row is the MEAN OF THE STUDENTS' percents, not a second pooling
     * across the roster. Pooling would weight each student by how much of their
     * work happens to be marked, so the half-marked back of the alphabet would
     * count less than the front - and "class average" everywhere else on this
     * page (the trajectory line, the sheet footer) already means the average
     * student, not the average mark. */
    const classAverage = {};
    for (const s of SECTIONS) {
      const vals = rows.map((r) => r.percent[s]).filter((p) => p != null);
      classAverage[s] = vals.length ? mean(vals) : null;
    }

    return { splits, rows, classAverage };
  }, [columns, gradebook]);

  /* No splits in view means no category evidence was ever promised - see the
   * file header for why the whole block disappears rather than showing zeroes. */
  const hasSplits = profile.splits.length > 0;

  /* Every student's dots for the achievement scatter: x = the category's slot
   * plus a small per-student jitter (deterministic - from roster index - so
   * nothing jumps between renders), y = their pooled percent. One entry per
   * STUDENT, not per dot, keeps hover and click at student grain: touching any
   * of their dots is touching them, which is what lets all four light up. */
  const catPoints = useMemo(
    () => profile.rows.map((r, i) => ({
      ...r,
      points: SECTIONS.flatMap((s, si) =>
        (r.percent[s] == null ? [] : [{ x: si + ((i % 7) - 3) * 0.055, y: r.percent[s] }])),
    })),
    [profile],
  );

  /* ── Student lines ───────────────────────────────────────────────────── */
  /* One row per assessment, carrying the class average plus a column per
   * student, keyed by id. Recharts reads each `dataKey` straight off the row. */
  const trajectory = useMemo(
    () => columns.map((a) => {
      const row = { name: truncateLabel(a.title, 16) };
      const all = [];
      for (const s of gradebook.students) {
        const p = markPercent(gradebook.marks[`${s.id}:${a.id}`], a);
        if (p != null) all.push(p);
        row[s.id] = p == null ? null : Math.round(p);
      }
      row.average = all.length ? Math.round(mean(all)) : null;
      return row;
    }),
    [gradebook, columns],
  );

  /* ── One assessment's spread ─────────────────────────────────────────── */
  /* Falls back to the first column in view rather than to nothing. The picked
   * assessment can leave the range under the date filter, and without the
   * fallback the panel would answer a legal filter change with "No assessments
   * yet" while a list of assessments sat beside it. Derived, not an effect: an
   * effect would need a render to notice, so the panel would blink empty first. */
  const focused = columns.find((c) => c.id === focusAssessment) ?? columns[0] ?? null;

  const histogram = useMemo(() => {
    const a = focused;
    if (!a) return { bins: [], marked: 0 };
    const scored = gradebook.students
      .map((s) => ({ name: s.name, p: markPercent(gradebook.marks[`${s.id}:${a.id}`], a) }))
      .filter((x) => x.p != null);
    return {
      bins: BINS.map((b) => {
        const members = scored.filter((x) => x.p >= b.min && x.p <= b.max);
        return { label: b.label, students: members.length, names: members.map((x) => x.name) };
      }),
      marked: scored.length,
    };
  }, [gradebook, focused]);
  const hoveredStudent = gradebook.students.find((s) => s.id === hovered);
  const base = `/gradebooks/${gradebook.id}`;

  const allPicked = picked.size === gradebook.students.length;
  const toggle = (id) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <PageContainer size="wide">
      <div className="mb-5">
        <Link
          to={base}
          className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground hover:underline"
        >
          <ArrowLeft size={14} /> Back to Gradebook
        </Link>
        <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
          <h1 className="font-display text-3xl leading-tight text-foreground">Insights</h1>
          <DateRangeFilter
            range={range}
            onChange={setRange}
            fullRange={fullRange}
            hiddenCount={assessments.length - columns.length}
          />
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-4">
        {/* FIRST, because it is the question a teacher opens this page with:
            who is moving, and which way. Straight segments between the points,
            not a curve - each vertex is a real assessment, and the line claims
            nothing about the space in between. Every line is the same hue on
            purpose - see the file header. The name appears in the panel note
            on hover, which is why there is no legend and no per-point label. */}
        <Panel
          title="Students Over the Year"
          note={hoveredStudent
            ? hoveredStudent.name
            : `${picked.size} of ${gradebook.students.length} shown`}
          className="lg:col-span-4"
        >
          <div className="flex flex-col gap-4 sm:flex-row">
            <PickList
              actions={
                <button
                  type="button"
                  onClick={() => setPicked(allPicked ? new Set() : new Set(gradebook.students.map((s) => s.id)))}
                  className="mb-2 self-start text-[12px] font-medium text-primary hover:underline"
                >
                  {allPicked ? "Clear All" : "Select All"}
                </button>
              }
            >
              {gradebook.students.map((s) => (
                <label
                  key={s.id}
                  onMouseEnter={() => setHovered(s.id)}
                  onMouseLeave={() => setHovered(null)}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 border-b border-border/60 px-2 py-1.5 text-[13px] last:border-0",
                    hovered === s.id ? "bg-muted" : "hover:bg-muted/60",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={picked.has(s.id)}
                    onChange={() => toggle(s.id)}
                    className="h-3.5 w-3.5 shrink-0 accent-[hsl(var(--brand-600))]"
                  />
                  <span className="truncate text-foreground">{s.name}</span>
                </label>
              ))}
            </PickList>

            <div className="h-[280px] min-w-0 flex-1">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trajectory} margin={{ top: 4, right: 8, left: -24, bottom: 44 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                  <XAxis
                    dataKey="name" interval={0} angle={-35} textAnchor="end" height={44}
                    tickLine={false} axisLine={false} tick={{ ...TICK, fontSize: 11 }}
                  />
                  <YAxis domain={[0, 100]} tickLine={false} axisLine={false} tick={TICK} />
                  <ReferenceLine y={boundary} stroke={GRID} strokeDasharray="2 4" />

                  {/* Drawn first so student lines sit above it. */}
                  <Line
                    type="linear" dataKey="average" stroke={REFERENCE}
                    strokeWidth={2} strokeDasharray="4 4" dot={false} connectNulls
                    isAnimationActive={false}
                  />

                  {gradebook.students.filter((s) => picked.has(s.id)).map((s) => (
                    <Line
                      key={s.id}
                      type="linear"
                      dataKey={s.id}
                      stroke={SERIES}
                      /* Hovering one line fades the rest rather than recolouring
                         anything: with a full class on screen the only readable
                         highlight is contrast, not hue. */
                      strokeOpacity={hovered == null ? 0.55 : hovered === s.id ? 1 : 0.12}
                      strokeWidth={hovered === s.id ? 3 : 2}
                      dot={false}
                      activeDot={{ r: 4 }}
                      connectNulls
                      isAnimationActive={false}
                      onMouseEnter={() => setHovered(s.id)}
                      onMouseLeave={() => setHovered(null)}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <p className="mt-2 flex items-center justify-end gap-1.5 text-[11px] text-muted-foreground">
            <span className="h-0.5 w-4 border-t-2 border-dashed" style={{ borderColor: REFERENCE }} />
            Class average
          </p>
        </Panel>

        {/* One assessment at a time: two histograms side by side would invite
            comparing bin heights across different denominators. */}
        <Panel
          title="Student Distribution per Assessment"
          note={focused ? `${histogram.marked} marked · out of ${focused.max}` : null}
          className="lg:col-span-4"
        >
          <div className="flex flex-col gap-4 sm:flex-row">
            <PickList>
              {columns.map((a) => (
                <label
                  key={a.id}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 border-b border-border/60 px-2 py-1.5 text-[13px] last:border-0",
                    a.id === focused?.id ? "bg-muted font-medium" : "hover:bg-muted/60",
                  )}
                >
                  <input
                    type="radio"
                    name="focus-assessment"
                    checked={a.id === focused?.id}
                    onChange={() => setFocusAssessment(a.id)}
                    className="h-3.5 w-3.5 shrink-0 accent-[hsl(var(--brand-600))]"
                  />
                  <span className="truncate text-foreground">{a.title || "Untitled"}</span>
                </label>
              ))}
            </PickList>

            <div className="h-[240px] min-w-0 flex-1">
              {!focused ? (
                <p className="text-[13px] text-muted-foreground">No assessments yet.</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={histogram.bins} margin={{ top: 12, right: 8, left: -24, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                    <XAxis
                      dataKey="label" interval={0} tickLine={false} axisLine={false}
                      tick={{ ...TICK, fontSize: 11 }}
                    />
                    <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={TICK} />
                    {/* Names, not the count - the count already sits on the bar. */}
                    <Tooltip cursor={{ fill: "hsl(var(--muted-foreground)/0.08)" }} content={<NamesTip />} />
                    <ReferenceLine x={BINS.find((b) => boundary >= b.min && boundary <= b.max)?.label} stroke={GRID} strokeDasharray="2 4" />
                    <Bar
                      dataKey="students"
                      fill={SERIES}
                      radius={[4, 4, 0, 0]}
                      maxBarSize={28}
                      label={BAR_LABEL}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </Panel>

        {/* One series, so no legend - the title names it - and the count sits on
            each bar rather than being left to the axis. Thirteen letter bins,
            so the minus and plus sub-bands read separately. */}
        <Panel
          title="Class Distribution"
          note={`${graded.length} with a grade`}
          className="lg:col-span-3"
        >
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={distribution} margin={{ top: 12, right: 8, left: -24, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                <XAxis dataKey="band" interval={0} tickLine={false} axisLine={false} tick={{ ...TICK, fontSize: 12 }} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={TICK} />
                {/* Names, not the count - the count already sits on the bar. */}
                <Tooltip cursor={{ fill: "hsl(var(--muted-foreground)/0.08)" }} content={<NamesTip />} />
                <Bar
                  dataKey="students"
                  fill={SERIES}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={30}
                  label={BAR_LABEL}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        {/* Not a chart: a short list of names to act on. The boundary input IS
            the title - "Students below 50%" says the whole thing, and the
            near-the-line names below it are simply the grey ones. */}
        <Panel
          title={
            <span className="flex items-baseline gap-2">
              Students Below
              <input
                type="number"
                min={0}
                max={100}
                value={boundary}
                onChange={(e) => setBoundary(Math.min(100, Math.max(0, Number(e.target.value) || 0)))}
                className="h-7 w-14 rounded-md border border-border bg-background px-1.5 text-right font-sans text-[14px] tabular-nums text-foreground outline-none focus:border-primary"
              />
              %
            </span>
          }
        >
          {atRisk.length === 0 ? (
            <p className="text-[13px] text-muted-foreground">Nobody is near {boundary}%.</p>
          ) : (
            <ul className="max-h-[196px] space-y-1 overflow-y-auto">
              {atRisk.map((r) => (
                <li
                  key={r.student.id}
                  className="flex items-baseline justify-between gap-2 border-b border-border/60 pb-1 text-[14px] last:border-0"
                >
                  <span className="truncate text-foreground">{r.student.name}</span>
                  <span
                    className={cn(
                      "flex shrink-0 items-baseline font-semibold",
                      // Below the line is a different state from near it, and the
                      // number alone does not say which - so it is stated in ink,
                      // not left to a colour a teacher has to interpret.
                      r.below ? "text-destructive" : "text-muted-foreground",
                    )}
                    title={r.below ? `Below ${boundary}%` : `Within ${NEAR}% of ${boundary}%`}
                  >
                    {/* Two fixed-width halves, matching the sheet's Grade
                        column: the letter is 1-2 glyphs ("R" vs "D-"), so one
                        centred string let its width shove the percentage
                        sideways and no two rows lined up. */}
                    <span className="w-[2.6rem] text-right">{Math.round(r.percent)}%</span>
                    <span className="ml-1.5 w-[1.6rem] text-left">{percentToLetter(r.percent)}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        {/* The profile the whole K/T/C/A migration was for - every student's
            pooled percent in every category, as POINTS. The class shape is the
            cloud itself; a summary boxplot would average away the two outliers
            a teacher actually needs to find. Hover a dot and all of that
            student's dots light up, with their numbers on the card rail beside
            the chart; clicking pins them there to compare. Hover and pins are
            this panel's own state - nothing here selects anything in the other
            panels. */}
        {hasSplits && (
          <Panel
            title="Achievement Categories"
            note={`${profile.splits.length} of ${columns.length} assessments split`}
            className="lg:col-span-4"
          >
            <div className="flex flex-col gap-5 sm:flex-row">
              <div className="h-[300px] min-w-0 flex-1">
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart margin={{ top: 12, right: 8, left: -24, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                    <XAxis
                      type="number"
                      dataKey="x"
                      domain={[-0.5, 3.5]}
                      ticks={[0, 1, 2, 3]}
                      tickLine={false}
                      axisLine={false}
                      height={40}
                      tick={<SectionTick />}
                    />
                    <YAxis type="number" dataKey="y" domain={[0, 100]} tickLine={false} axisLine={false} tick={TICK} />
                    <ReferenceLine y={boundary} stroke={GRID} strokeDasharray="2 4" />
                    {catPoints.map((r) => {
                      const id = r.student.id;
                      const lit = catHovered === id || pinned.has(id);
                      const anyFocus = catHovered != null || pinned.size > 0;
                      return (
                        <Scatter
                          key={id}
                          data={r.points}
                          isAnimationActive={false}
                          onMouseEnter={() => setCatHovered(id)}
                          onMouseLeave={() => setCatHovered(null)}
                          onClick={() => togglePin(id)}
                          className="cursor-pointer"
                          /* The dot is drawn from the closure, not recharts'
                             fill props, so one student's state styles all four
                             of their dots at once. */
                          shape={(p) => (
                            <circle
                              cx={p.cx}
                              cy={p.cy}
                              r={lit ? 6 : 4.5}
                              fill={pinned.has(id) ? PINNED : SERIES}
                              fillOpacity={lit ? 1 : anyFocus ? 0.18 : 0.5}
                            />
                          )}
                        />
                      );
                    })}
                  </ScatterChart>
                </ResponsiveContainer>
              </div>

              {/* The card rail the hover reads out to. Class average first -
                  everything is read against it - then the pins, then whatever
                  the pointer is currently over. */}
              <div className="flex w-full shrink-0 flex-col gap-2 sm:max-h-[300px] sm:w-[260px] sm:overflow-y-auto">
                <ProfileCard label="Class Average" values={profile.classAverage} muted />
                {catPoints.filter((r) => pinned.has(r.student.id)).map((r) => (
                  <ProfileCard
                    key={r.student.id}
                    label={r.student.name}
                    values={r.percent}
                    pinned
                    onUnpin={() => togglePin(r.student.id)}
                  />
                ))}
                {catHovered != null && !pinned.has(catHovered) && (() => {
                  const r = catPoints.find((x) => x.student.id === catHovered);
                  return r ? <ProfileCard label={r.student.name} values={r.percent} /> : null;
                })()}
              </div>
            </div>
          </Panel>
        )}
      </div>
    </PageContainer>
  );
}
